import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import {
  CardCarousel,
  CardGrid,
  MOBILE_PRODUCT_CARD_WIDTH,
} from '@iheartjane/menu';
import { Flex, Loading } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router';

import { ProductCard } from '@jane/bloom/components/productCard/productCard';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getThemedProductCardWidth } from '@jane/bloom/utils/product/getThemedProductCardWidth';
import { advanceLoadedPageInUrl } from '@jane/bloom/utils/queryParams/loadedPage';
import type {
  ProductFilterFunction,
  ShouldShowWeightFilter,
} from '@jane/bloom/utils/specials/shouldShowWeight';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import { buildSingleProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearchResult';

import styles from './productGrid.module.css';

/* a11y: SSR prevents us from knowing the number of rendered product card columns
 so this value functions as a best guess to insert the mid-grid ad closest to
 its actual rendered position in the DOM for screen readers */
const DEFAULT_NUM_COLUMNS = 4;

export const MENU_RESULTS_ELEMENT_ID = 'results';

type MenuRowProps = {
  applicablePriceIds?: ProductApplicableIds[] | null;
  brandSpecials: BrandSpecials;
  customLanguage: CustomLanguage;
  dynamicPlacement?: SmartSort<MaterializedMenuProduct>;
  janeGold: JaneGold;
  maxWeightOption?: number | null;
  preloadedProductIds?: number[];
  productFilter?: ProductFilterFunction;
  rowInstance: SmartSort<MaterializedMenuProduct>;
  specials: Nullable<StoreSpecials>;
  weightFilter?: ShouldShowWeightFilter;
};

export const ProductGrid = ({
  applicablePriceIds,
  brandSpecials,
  customLanguage,
  dynamicPlacement,
  janeGold,
  maxWeightOption,
  preloadedProductIds,
  productFilter,
  rowInstance,
  specials,
  weightFilter,
}: MenuRowProps) => {
  const { cart, featureFlags, pinnedStore, theme } = useRootLoaderData();
  const midGridAdRow = featureFlags[FLAGS.c3MidGridAdRow];

  const cardWidth = getThemedProductCardWidth(featureFlags, theme);
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasMoreFilteredProducts) {
        loadMore();
      }
    },
  });
  const [searchParams] = useSearchParams();
  const [hasMoreProducts, setHasMoreProducts] = useState(
    rowInstance.hasNextPage()
  );
  const [instanceProducts, setProducts] = useState(rowInstance.products);

  const showPlacement = midGridAdRow && dynamicPlacement;
  const placementIndex = DEFAULT_NUM_COLUMNS * 2;

  useEffect(() => {
    /* when the search params change, use the new row instance */
    setHasMoreProducts(rowInstance.hasNextPage());
    setProducts([...rowInstance.products]);
  }, [searchParams, rowInstance]);

  const loadMore = useCallback(async () => {
    await rowInstance.nextPage();
    setHasMoreProducts(rowInstance.hasNextPage());
    setProducts([...rowInstance.products]);
    advanceLoadedPageInUrl();
  }, [rowInstance]);

  const products = useMemo(() => {
    return productFilter ? productFilter(instanceProducts) : instanceProducts;
  }, [productFilter, instanceProducts]);

  // When filtering, we need to determine if there are more products to show
  // Even if backend has more pages, if filtering removes all products, we shouldn't show loading
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const hasMoreFilteredProducts = useMemo(() => {
    if (!productFilter) {
      return hasMoreProducts;
    }
    if (products.length === 0 && instanceProducts.length > 0) {
      return false;
    }
    return hasMoreProducts;
  }, [
    productFilter,
    hasMoreProducts,
    products.length,
    instanceProducts.length,
  ]);

  useEffect(() => {
    rowInstance.onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dynamicPlacement?.onLoad();
  }, [dynamicPlacement]);

  // Auto-load more products if filtering reduced the list significantly, ensures we show products even when many are filtered out
  useEffect(() => {
    if (
      productFilter &&
      hasMoreFilteredProducts &&
      instanceProducts.length > 0 &&
      products.length < 10 &&
      instanceProducts.length > products.length
    ) {
      loadMore();
    }
  }, [
    productFilter,
    hasMoreFilteredProducts,
    instanceProducts.length,
    products.length,
    loadMore,
  ]);

  const gridChildren = useMemo(() => {
    if (products.length === 0) {
      return null;
    }
    const cards = products.map((product) => {
      // When a weight filter is active (custom row price threshold or store-special
      // conditions), compute per-product filtered weights so price labels only
      // reflect valid weights. Store specials fold their applicable price IDs into
      // weightFilter via showOnlyWeights, so a single pass covers both the special
      // restriction and the cart-bypass. For brand specials (no weightFilter) we fall
      // back to the raw applicablePriceIds.
      const weightFilteredIds = weightFilter
        ? (product.attributes.available_weights?.filter((weight) =>
            weightFilter(weight, product)
          ) as ProductApplicableIds[])
        : undefined;

      const builtProduct = buildSingleProductSearchResultViewModel({
        applicablePriceIds: weightFilteredIds ?? applicablePriceIds,
        bundleTag: featureFlags[FLAGS.c1BundleTag],
        cart,
        customLanguage,
        janeGold,
        pinnedStore,
        product: product.attributes,
        specials,
      });

      return (
        <ProductCard
          brandSpecials={brandSpecials}
          key={product.objectId}
          isSponsored={product.isSponsored}
          maxWeightOption={maxWeightOption}
          product={builtProduct}
          selected={() => {
            product.click();
          }}
          weightFilter={weightFilter}
          preloaded={preloadedProductIds?.includes(
            product.attributes['product_id']
          )}
          smartProductInstance={product}
        />
      );
    });

    const placementElement: ReactNode = showPlacement ? (
      <CardGrid.Placement key="mid-grid-placement" background="primary" py={24}>
        <CardCarousel
          name={dynamicPlacement.title ?? ''}
          mode="min-width"
          cardWidth={cardWidth}
          cardWidthMobile={MOBILE_PRODUCT_CARD_WIDTH}
          cardGap={24}
          cardGapMobile={16}
          mobileScrollPadding={16}
        >
          {dynamicPlacement.products.map((product) => {
            const builtProduct = buildSingleProductSearchResultViewModel({
              bundleTag: featureFlags[FLAGS.c1BundleTag],
              cart,
              customLanguage,
              janeGold,
              pinnedStore,
              product: product.attributes,
              specials,
            });

            return (
              <CardCarousel.Card
                key={`dyn-${product.objectId}`}
                width="100%"
                widthMobile="100%"
              >
                <ProductCard
                  brandSpecials={brandSpecials}
                  isPlacement
                  isSponsored={product.isSponsored}
                  product={builtProduct}
                  selected={() => {
                    product.click();
                  }}
                  smartProductInstance={product}
                />
              </CardCarousel.Card>
            );
          })}
        </CardCarousel>
      </CardGrid.Placement>
    ) : null;

    if (!placementElement) return cards;

    /* a11y: insert carousel at correct DOM position in the grid */
    const insertAt = Math.min(placementIndex, cards.length);
    return [
      ...cards.slice(0, insertAt),
      placementElement,
      ...cards.slice(insertAt),
    ];
  }, [
    applicablePriceIds,
    brandSpecials,
    cart,
    customLanguage,
    dynamicPlacement?.products,
    dynamicPlacement?.title,
    featureFlags,
    janeGold,
    pinnedStore,
    placementIndex,
    preloadedProductIds,
    products,
    showPlacement,
    specials,
    weightFilter,
  ]);

  return (
    <>
      <CardGrid
        id={MENU_RESULTS_ELEMENT_ID}
        minCardWidth={cardWidth}
        gap={24}
        mobileGap={16}
        className={styles['productGridContainer']}
      >
        {gridChildren}
      </CardGrid>

      <div ref={ref} aria-hidden="true" />
      {hasMoreFilteredProducts && (
        <Flex position="relative" mt={24}>
          <Loading color="black" />
        </Flex>
      )}
    </>
  );
};
